import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scan, Plus, Edit3, Trash2, Eye, EyeOff, Star, StarOff,
  Save, X, Loader2, CheckCircle, AlertTriangle, ArrowLeft,
  ExternalLink, Image, Upload, Copy, Check, Link, Youtube
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'tutorial', label: 'Tutorial' },
  { value: 'news', label: 'Noticias IA' },
  { value: 'use-case', label: 'Caso de uso' },
  { value: 'update', label: 'Novedad' },
];

const EMOJIS = ['📝', '🤖', '🔍', '🛡️', '🎬', '🖼️', '📄', '⚡', '🧠', '🎯', '🔬', '📰', '🏢', '🎓', '💡', '🚀'];

function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const EMPTY_FORM = {
  title: '', slug: '', excerpt: '',
  content: `## Introducción\n\nEscribe aquí la introducción. Puedes usar **negrita**, *cursiva* y \`código inline\`.\n\n## Sección principal\n\nDesarrolla tu contenido aquí.\n\n## Conclusión\n\nResume los puntos clave del artículo.\n\n> 💡 Consejo: usa Veritas Scan para detectar contenido IA en segundos.`,
  category: 'tutorial', tags: '', cover_emoji: '📝', cover_image: '',
  published: false, featured: false,
};

// ─── Image Uploader ────────────────────────────────────────────────────────────
// Recibe textareaRef y setContent directamente para insertar sin perder cursor
function ImageUploader({ textareaRef, setContent }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [altInput, setAltInput] = useState('');
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  // Snapshot of cursor taken at the moment the button is pressed (mousedown)
  const insertPosRef = useRef({ start: 0, end: 0 });

  const reset = () => {
    setUploading(false); setUploadError(null);
    setUrlInput(''); setAltInput('');
    setUploadedUrl(null); setCopied(false); setTab('upload');
  };
  const close = () => { setOpen(false); reset(); };

  // Called on mousedown of the trigger button — BEFORE the textarea loses focus
  const snapCursor = () => {
    const ta = textareaRef.current;
    if (ta) {
      insertPosRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
    }
  };

  const uploadToCloudinary = async (file) => {
    setUploading(true); setUploadError(null);
    try {
      const token = localStorage.getItem('veritas_token');
      if (!token) throw new Error('No estás autenticado');
      const sigRes = await axios.get(`${API_URL}/api/blog/upload-signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { timestamp, signature, api_key, cloud_name, folder } = sigRes.data;
      if (!cloud_name) throw new Error('Cloudinary no configurado. Añade CLOUDINARY_* en Railway.');

      const fd = new FormData();
      fd.append('file', file);
      fd.append('timestamp', String(timestamp));
      fd.append('signature', signature);
      fd.append('api_key', api_key);
      fd.append('folder', folder);

      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `Error ${res.status}`);
      }
      const data = await res.json();
      if (!data.secure_url) throw new Error('Cloudinary no devolvió URL');
      setUploadedUrl(data.secure_url);
      setUrlInput(data.secure_url);
    } catch (e) {
      setUploadError(e.message || 'Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) { setUploadError(null); uploadToCloudinary(file); }
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file?.type.startsWith('image/')) uploadToCloudinary(file);
  };

  const activeUrl = uploadedUrl || (tab === 'url' ? urlInput.trim() : '');

  // Insert the markdown at the snapshotted cursor position
  const handleInsert = () => {
    if (!activeUrl) return;
    const markdown = `![${altInput.trim() || 'imagen'}](${activeUrl})`;
    const ta = textareaRef.current;
    const current = ta ? ta.value : '';
    const { start, end } = insertPosRef.current;
    const before = current.substring(0, start);
    const after = current.substring(end);
    // Ensure image is on its own line
    const needsBefore = before.length > 0 && !before.endsWith('\n');
    const needsAfter = after.length > 0 && !after.startsWith('\n');
    const inserted = (needsBefore ? '\n' : '') + markdown + (needsAfter ? '\n' : '');
    const newContent = before + inserted + after;
    const newPos = start + inserted.length;

    // Update React state
    setContent(newContent);

    // Restore cursor after re-render
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    });
    close();
  };

  const copyMarkdown = () => {
    if (!activeUrl) return;
    navigator.clipboard.writeText(`![${altInput.trim() || 'imagen'}](${activeUrl})`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* Trigger — snap cursor on mousedown, open on click */}
      <button
        type="button"
        onMouseDown={snapCursor}
        onClick={() => setOpen(true)}
        title="Insertar imagen donde está el cursor"
        className="flex items-center gap-1.5 text-zinc-400 hover:text-blue-400 font-mono text-xs transition-colors px-2 py-1 rounded hover:bg-zinc-700 border border-zinc-700 hover:border-blue-500"
      >
        <Image className="w-3.5 h-3.5" /> Imagen
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/75 z-50" onClick={close} />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.16 }}
              className="fixed left-1/2 top-16 -translate-x-1/2 z-50 bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col"
              style={{ maxHeight: 'calc(100vh - 5rem)' }}
            >
              {/* Header — fijo */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-800 flex-shrink-0">
                <div>
                  <h3 className="font-heading text-lg font-bold text-white">Insertar imagen</h3>
                  <p className="text-zinc-600 text-xs font-mono mt-0.5">
                    Se insertará en la posición {insertPosRef.current.start} del texto
                  </p>
                </div>
                <button onClick={close} className="text-zinc-500 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs — fijos */}
              <div className="flex border-b border-zinc-800 flex-shrink-0">
                <button
                  onClick={() => { setTab('upload'); setUploadedUrl(null); setUploadError(null); }}
                  className={`flex-1 py-3 font-mono text-sm transition-colors flex items-center justify-center gap-2 ${tab === 'upload' ? 'text-blue-400 border-b-2 border-blue-500 bg-blue-500/5' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <Upload className="w-4 h-4" /> Subir archivo
                </button>
                <button
                  onClick={() => { setTab('url'); setUploadedUrl(null); setUploadError(null); }}
                  className={`flex-1 py-3 font-mono text-sm transition-colors flex items-center justify-center gap-2 ${tab === 'url' ? 'text-blue-400 border-b-2 border-blue-500 bg-blue-500/5' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <Link className="w-4 h-4" /> Pegar URL
                </button>
              </div>

              {/* Contenido scrolleable */}
              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Upload tab */}
                {tab === 'upload' && (
                  <div>
                    {!uploadedUrl ? (
                      <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={handleDrop}
                        onClick={() => !uploading && fileRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer select-none ${
                          uploading ? 'border-blue-500 bg-blue-500/5 cursor-wait' : 'border-zinc-700 hover:border-blue-500 hover:bg-zinc-800/50'
                        }`}
                      >
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                        {uploading ? (
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                            <p className="text-zinc-400 text-sm font-mono">Subiendo a Cloudinary...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-14 h-14 rounded-2xl bg-zinc-800 flex items-center justify-center">
                              <Upload className="w-7 h-7 text-zinc-400" />
                            </div>
                            <div>
                              <p className="text-white text-sm font-semibold">Arrastra una imagen aquí</p>
                              <p className="text-zinc-500 text-xs mt-1">o haz clic para seleccionar</p>
                            </div>
                            <p className="text-zinc-600 text-xs font-mono">JPG · PNG · WebP · GIF — máx. 10 MB</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-zinc-800 rounded-xl p-3 border border-zinc-700">
                          <img src={uploadedUrl} alt="preview" className="max-h-40 mx-auto rounded-lg object-contain" />
                        </div>
                        <div className="flex items-center gap-2 text-emerald-400 text-sm font-mono bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" /> Imagen subida correctamente
                        </div>
                        <button type="button" onClick={() => { setUploadedUrl(null); setUrlInput(''); }}
                          className="text-zinc-500 hover:text-zinc-300 text-xs font-mono underline">
                          Subir otra imagen
                        </button>
                      </div>
                    )}
                    {uploadError && (
                      <div className="mt-3 flex items-start gap-2 text-red-400 text-xs font-mono bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{uploadError}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* URL tab */}
                {tab === 'url' && (
                  <div className="space-y-3">
                    <div>
                      <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-1.5">URL de la imagen</label>
                      <input
                        autoFocus
                        value={urlInput}
                        onChange={e => setUrlInput(e.target.value)}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-lg px-3 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                      />
                    </div>
                    {urlInput && (
                      <div className="bg-zinc-800 rounded-xl p-3 border border-zinc-700">
                        <img src={urlInput} alt="preview" className="max-h-32 mx-auto rounded object-contain"
                          onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                )}

                {/* Alt text */}
                <div>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-1.5">
                    Texto alternativo <span className="text-zinc-600 normal-case font-normal">(SEO + accesibilidad)</span>
                  </label>
                  <input
                    value={altInput}
                    onChange={e => setAltInput(e.target.value)}
                    placeholder="Descripción breve de la imagen"
                    className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-lg px-3 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                  />
                </div>

                {/* Markdown preview */}
                {activeUrl && (
                  <div className="flex items-center gap-2 bg-zinc-800/80 border border-zinc-700 rounded-lg px-3 py-2">
                    <code className="text-blue-300 font-mono text-xs truncate flex-1">
                      ![{altInput.trim() || 'imagen'}]({activeUrl.length > 42 ? activeUrl.slice(0, 42) + '…' : activeUrl})
                    </code>
                    <button type="button" onClick={copyMarkdown}
                      className="text-zinc-500 hover:text-white flex-shrink-0 transition-colors" title="Copiar markdown">
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={close}
                    className="flex-1 py-2.5 border border-zinc-700 hover:border-zinc-600 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                    Cancelar
                  </button>
                  <button type="button" onClick={handleInsert} disabled={!activeUrl}
                    className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-mono text-sm rounded-xl transition-all font-medium">
                    Insertar en el texto
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── YouTube helper ───────────────────────────────────────────────────────────
function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

// ─── YouTube Embed component ───────────────────────────────────────────────────
function YouTubeEmbed({ videoId }) {
  return (
    <div className="my-6 rounded-xl overflow-hidden border border-zinc-800 bg-black" style={{ aspectRatio: '16/9' }}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
        style={{ border: 'none' }}
      />
    </div>
  );
}

// ─── YouTube Inserter ──────────────────────────────────────────────────────────
function YouTubeInserter({ textareaRef, setContent }) {
  const [open, setOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState('');
  const insertPosRef = useRef({ start: 0, end: 0 });

  const snapCursor = () => {
    const ta = textareaRef.current;
    if (ta) insertPosRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
  };

  const close = () => { setOpen(false); setUrlInput(''); setError(''); };

  const videoId = urlInput.trim() ? extractYouTubeId(urlInput.trim()) : null;

  const handleInsert = () => {
    if (!videoId) { setError('URL de YouTube no válida'); return; }
    const markdown = `[youtube:${videoId}]`;
    const ta = textareaRef.current;
    const current = ta ? ta.value : '';
    const { start, end } = insertPosRef.current;
    const before = current.substring(0, start);
    const after = current.substring(end);
    const needsBefore = before.length > 0 && !before.endsWith('\n');
    const needsAfter = after.length > 0 && !after.startsWith('\n');
    const inserted = (needsBefore ? '\n' : '') + markdown + (needsAfter ? '\n' : '');
    const newContent = before + inserted + after;
    const newPos = start + inserted.length;
    setContent(newContent);
    requestAnimationFrame(() => {
      if (ta) { ta.focus(); ta.setSelectionRange(newPos, newPos); }
    });
    close();
  };

  return (
    <>
      <button
        type="button"
        onMouseDown={snapCursor}
        onClick={() => setOpen(true)}
        title="Insertar vídeo de YouTube"
        className="flex items-center gap-1.5 text-zinc-400 hover:text-red-400 font-mono text-xs transition-colors px-2 py-1 rounded hover:bg-zinc-700 border border-zinc-700 hover:border-red-500"
      >
        <Youtube className="w-3.5 h-3.5" /> YouTube
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/75 z-50" onClick={close} />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.16 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                    <Youtube className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white">Insertar vídeo de YouTube</h3>
                </div>
                <button onClick={close} className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-1.5">URL del vídeo</label>
                  <input
                    autoFocus
                    value={urlInput}
                    onChange={e => { setUrlInput(e.target.value); setError(''); }}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full bg-zinc-800 border border-zinc-700 focus:border-red-500 rounded-lg px-3 py-2.5 text-zinc-200 placeholder-zinc-600 font-mono text-sm outline-none transition-colors"
                  />
                  {error && <p className="text-red-400 text-xs font-mono mt-1.5">{error}</p>}
                </div>

                {/* Preview */}
                {videoId && (
                  <div className="rounded-xl overflow-hidden border border-zinc-700 bg-black" style={{ aspectRatio: '16/9' }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                      title="preview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      style={{ border: 'none' }}
                    />
                  </div>
                )}

                {!videoId && urlInput && (
                  <div className="flex items-center gap-2 text-amber-400 text-xs font-mono bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    No se reconoce como URL de YouTube válida
                  </div>
                )}

                {videoId && (
                  <div className="flex items-center gap-2 bg-zinc-800/80 border border-zinc-700 rounded-lg px-3 py-2">
                    <code className="text-red-300 font-mono text-xs flex-1">[youtube:{videoId}]</code>
                    <span className="text-zinc-600 text-xs font-mono">se insertará en el texto</span>
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={close}
                    className="flex-1 py-2.5 border border-zinc-700 hover:border-zinc-600 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all">
                    Cancelar
                  </button>
                  <button type="button" onClick={handleInsert} disabled={!videoId}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-mono text-sm rounded-xl transition-all font-medium">
                    Insertar vídeo
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Preview renderer ──────────────────────────────────────────────────────────
function PreviewContent({ content }) {
  const lines = (content || '').split('\n');
  let inCode = false, codeLines = [], codeLang = '';
  const elements = [];

  lines.forEach((line, i) => {
    if (line.startsWith('```')) {
      if (!inCode) { inCode = true; codeLang = line.slice(3).trim(); codeLines = []; }
      else {
        elements.push(
          <div key={i} className="my-4 rounded-xl overflow-hidden border border-zinc-700">
            {codeLang && <div className="bg-zinc-800 px-4 py-1.5 text-xs font-mono text-zinc-500 border-b border-zinc-700">{codeLang}</div>}
            <pre className="bg-zinc-900 px-4 py-3 overflow-x-auto text-sm font-mono text-zinc-300 leading-relaxed"><code>{codeLines.join('\n')}</code></pre>
          </div>
        );
        inCode = false; codeLines = []; codeLang = '';
      }
      return;
    }
    if (inCode) { codeLines.push(line); return; }

    // YouTube embed
    const ytMatch = line.match(/^\[youtube:([\w-]{11})\]$/);
    if (ytMatch) {
      elements.push(<YouTubeEmbed key={i} videoId={ytMatch[1]} />);
      return;
    }

    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(
        <figure key={i} className="my-6">
          <img src={imgMatch[2]} alt={imgMatch[1]} className="w-full rounded-xl border border-zinc-800 object-cover max-h-[480px]" loading="lazy" />
          {imgMatch[1] && imgMatch[1] !== 'imagen' && <figcaption className="text-center text-zinc-500 text-xs font-mono mt-2">{imgMatch[1]}</figcaption>}
        </figure>
      );
      return;
    }
    if (line.startsWith('# '))  { elements.push(<h1 key={i} className="font-heading text-3xl font-black text-white mt-8 mb-4">{line.slice(2)}</h1>); return; }
    if (line.startsWith('## ')) { elements.push(<h2 key={i} className="font-heading text-2xl font-bold text-white mt-6 mb-3 border-b border-zinc-800 pb-2">{line.slice(3)}</h2>); return; }
    if (line.startsWith('### ')){ elements.push(<h3 key={i} className="font-heading text-xl font-bold text-zinc-200 mt-4 mb-2">{line.slice(4)}</h3>); return; }
    if (line.startsWith('> '))  { elements.push(<blockquote key={i} className="border-l-4 border-blue-500 pl-4 py-1 my-3 text-zinc-300 italic bg-blue-500/5 rounded-r">{line.slice(2)}</blockquote>); return; }
    if (line.startsWith('- ') || line.startsWith('* ')) { elements.push(<p key={i} className="flex gap-2 text-zinc-300 my-1"><span className="text-blue-400 flex-shrink-0">•</span>{line.slice(2)}</p>); return; }
    if (line.startsWith('---')) { elements.push(<hr key={i} className="border-zinc-800 my-8" />); return; }
    if (line.trim() === '')     { elements.push(<div key={i} className="h-2" />); return; }
    elements.push(<p key={i} className="text-zinc-300 leading-relaxed my-1">{line}</p>);
  });

  return <div>{elements}</div>;
}

// ─── Cover Image Uploader ────────────────────────────────────────────────────
function CoverImageUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('upload');
  const [urlInput, setUrlInput] = useState(value || '');
  const fileRef = useRef(null);

  const uploadToCloudinary = async (file) => {
    setUploading(true); setError(null);
    try {
      const token = localStorage.getItem('veritas_token');
      const sigRes = await axios.get(`${API_URL}/api/blog/upload-signature`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { timestamp, signature, api_key, cloud_name, folder } = sigRes.data;
      if (!cloud_name) throw new Error('Cloudinary no configurado');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('timestamp', String(timestamp));
      fd.append('signature', signature);
      fd.append('api_key', api_key);
      fd.append('folder', folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      if (!data.secure_url) throw new Error('Sin URL');
      onChange(data.secure_url);
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const handleFile = (e) => { const f = e.target.files?.[0]; if (f) uploadToCloudinary(f); e.target.value = ''; };
  const handleDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f?.type.startsWith('image/')) uploadToCloudinary(f); };

  return (
    <div className="space-y-2">
      <div className="flex border border-zinc-700 rounded-lg overflow-hidden">
        {['upload','url'].map(t => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`flex-1 py-1.5 font-mono text-xs transition-colors ${
              tab === t ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
            }`}>
            {t === 'upload' ? 'Subir archivo' : 'URL'}
          </button>
        ))}
      </div>

      {tab === 'upload' && (
        <div
          onDragOver={e => e.preventDefault()} onDrop={handleDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
            uploading ? 'border-blue-500 bg-blue-500/5 cursor-wait' : 'border-zinc-700 hover:border-blue-500 hover:bg-zinc-800/50'
          }`}
        >
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <p className="text-zinc-400 text-xs font-mono">Subiendo...</p>
            </div>
          ) : value && tab === 'upload' ? (
            <div className="space-y-2">
              <img src={value} alt="portada" className="max-h-28 mx-auto rounded-lg object-cover w-full" />
              <p className="text-zinc-500 text-xs font-mono">Haz clic para cambiar</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              <Upload className="w-6 h-6 text-zinc-500" />
              <p className="text-zinc-400 text-xs">Arrastra o haz clic</p>
            </div>
          )}
        </div>
      )}

      {tab === 'url' && (
        <div className="space-y-2">
          <input
            value={urlInput}
            onChange={e => { setUrlInput(e.target.value); if (e.target.value) onChange(e.target.value); }}
            placeholder="https://ejemplo.com/portada.jpg"
            className="w-full bg-zinc-800 border border-zinc-700 focus:border-blue-500 rounded-lg px-3 py-2 text-zinc-200 placeholder-zinc-600 font-mono text-xs outline-none transition-colors"
          />
          {urlInput && <img src={urlInput} alt="preview" className="max-h-24 mx-auto rounded-lg object-cover w-full" onError={e => e.target.style.display='none'} />}
        </div>
      )}

      {value && (
        <div className="flex items-center gap-2">
          <div className="flex-1 text-xs font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1 truncate">
            <CheckCircle className="w-3 h-3 inline mr-1" />{value.length > 40 ? '...' + value.slice(-40) : value}
          </div>
          <button type="button" onClick={() => { onChange(''); setUrlInput(''); }}
            className="text-zinc-500 hover:text-red-400 transition-colors p-1">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {error && <p className="text-red-400 text-xs font-mono">{error}</p>}
    </div>
  );
}

// ─── Main BlogAdmin ────────────────────────────────────────────────────────────
export default function BlogAdmin({ onBack, onPost, user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => { fetchPosts(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/blog/posts`, {
        params: { limit: 100 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(res.data.posts);
    } catch { showToast('Error cargando posts', 'error'); }
    finally { setLoading(false); }
  };

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); setPreviewMode(false); };

  const openEdit = async (slug) => {
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/blog/posts/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      const p = res.data;
      setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content,
        category: p.category, tags: (p.tags || []).join(', '), cover_emoji: p.cover_emoji || '📝',
        cover_image: p.cover_image || '',
        published: p.published, featured: p.featured });
      setEditing(slug); setPreviewMode(false);
    } catch { showToast('Error cargando post', 'error'); }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim() || !form.content.trim()) {
      showToast('Título, slug y contenido son obligatorios', 'error'); return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('veritas_token');
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/blog/posts`, payload, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Post creado correctamente');
      } else {
        await axios.put(`${API_URL}/api/blog/posts/${editing}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        showToast('Post actualizado');
      }
      setEditing(null); fetchPosts();
    } catch (e) { showToast(e.response?.data?.detail || 'Error al guardar', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (slug) => {
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.delete(`${API_URL}/api/blog/posts/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      showToast('Post eliminado');
      setPosts(prev => prev.filter(p => p.slug !== slug));
      setDeleteConfirm(null);
    } catch { showToast('Error al eliminar', 'error'); }
  };

  const togglePublish = async (post) => {
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.put(`${API_URL}/api/blog/posts/${post.slug}`, { published: !post.published }, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(prev => prev.map(p => p.slug === post.slug ? { ...p, published: !p.published } : p));
      showToast(post.published ? 'Post despublicado' : 'Post publicado');
    } catch { showToast('Error', 'error'); }
  };

  const toggleFeatured = async (post) => {
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.put(`${API_URL}/api/blog/posts/${post.slug}`, { featured: !post.featured }, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(prev => prev.map(p => p.slug === post.slug ? { ...p, featured: !p.featured } : p));
      showToast(post.featured ? 'Quitado de destacados' : 'Marcado como destacado');
    } catch { showToast('Error', 'error'); }
  };

  const handleTitleChange = (title) => {
    setForm(f => ({ ...f, title, ...(editing === 'new' ? { slug: slugify(title) } : {}) }));
  };

  // Insert markdown at current cursor position in textarea
  const insertAtCursor = (markdownText) => {
    const ta = textareaRef.current;
    if (!ta) {
      setForm(f => ({ ...f, content: f.content + '\n' + markdownText }));
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = ta.value;
    const before = current.substring(0, start);
    const after = current.substring(end);
    const isImage = markdownText.startsWith('![');
    const needsBefore = isImage && before.length > 0 && !before.endsWith('\n');
    const needsAfter = isImage && after.length > 0 && !after.startsWith('\n');
    const inserted = (needsBefore ? '\n' : '') + markdownText + (needsAfter ? '\n' : '');
    const newContent = before + inserted + after;
    const newPos = start + inserted.length;

    setForm(f => ({ ...f, content: newContent }));
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    });
  };

  const TOOLBAR_BUTTONS = [
    { label: '# H1', insert: '# Título principal' },
    { label: '## H2', insert: '## Sección' },
    { label: '### H3', insert: '### Subsección' },
    { label: '**bold**', insert: '**texto**' },
    { label: '*italic*', insert: '*texto*' },
    { label: '`code`', insert: '`código`' },
    { label: '```block```', insert: '```bash\ntu código aquí\n```' },
    { label: '- lista', insert: '- Elemento 1\n- Elemento 2\n- Elemento 3' },
    { label: '> cita', insert: '> 💡 Texto aquí' },
    { label: '---', insert: '---' },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 relative">
      <div className="scanlines" />

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border font-mono text-sm shadow-2xl ${
              toast.type === 'error' ? 'bg-red-900/90 border-red-500/50 text-red-200' : 'bg-emerald-900/90 border-emerald-500/50 text-emerald-200'
            }`}>
            {toast.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <header className="border-b border-zinc-800 sticky top-0 z-30 bg-zinc-950/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-4">
          <button onClick={() => editing ? setEditing(null) : onBack()}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-mono text-xs sm:text-sm flex-shrink-0">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{editing ? 'Volver a lista' : 'Volver al blog'}</span>
          </button>
          <div className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <span className="font-heading text-base sm:text-lg font-black whitespace-nowrap">VERITAS <span className="text-blue-500">SCAN</span></span>
            <span className="hidden sm:inline text-xs font-mono bg-zinc-800 border border-zinc-700 text-zinc-400 px-2 py-0.5 rounded">Admin Blog</span>
          </div>
          {!editing && (
            <button onClick={openNew}
              className="ml-auto flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs sm:text-sm uppercase tracking-wider rounded-lg transition-all whitespace-nowrap">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nuevo</span> POST
            </button>
          )}
          {editing && (
            <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
              <button onClick={() => setPreviewMode(!previewMode)}
                className={`px-2 sm:px-3 py-2 border font-mono text-xs rounded-lg transition-all ${previewMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
                {previewMode ? 'Editor' : 'Preview'}
              </button>
              <button onClick={() => setEditing(null)}
                className="hidden sm:block px-3 py-2 border border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs rounded-lg transition-all">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white font-mono text-xs sm:text-sm rounded-lg transition-all whitespace-nowrap">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span className="hidden sm:inline">{saving ? 'Guardando...' : 'Guardar'}</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8">

        {/* LIST */}
        {!editing && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-black">
                Posts del blog <span className="ml-3 text-sm font-mono text-zinc-500">{posts.length} total</span>
              </h1>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
            ) : posts.length === 0 ? (
              <div className="text-center py-24 space-y-4">
                <p className="text-5xl">✍️</p>
                <p className="font-heading text-xl text-zinc-400">Aún no hay posts</p>
                <button onClick={openNew} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm rounded-lg transition-all">Crear el primer post</button>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {posts.map((post, i) => (
                  <motion.div key={post.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl px-3 sm:px-5 py-3 sm:py-4 transition-all">
                    <span className="text-2xl sm:text-3xl flex-shrink-0">{post.cover_emoji || '📝'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className={`text-[10px] sm:text-xs font-mono px-1.5 py-0.5 rounded-full border ${
                          { tutorial: 'bg-blue-500/20 text-blue-400 border-blue-500/30', news: 'bg-purple-500/20 text-purple-400 border-purple-500/30', 'use-case': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', update: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }[post.category] || 'bg-zinc-700/30 text-zinc-400 border-zinc-700'
                        }`}>{CATEGORIES.find(c => c.value === post.category)?.label || post.category}</span>
                        {post.featured && <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />}
                        {!post.published && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">Borrador</span>}
                      </div>
                      <p className="text-white text-sm font-medium truncate">{post.title}</p>
                      <p className="text-zinc-500 text-[10px] sm:text-xs font-mono mt-0.5 truncate">/blog/{post.slug} · {post.views || 0} vistas</p>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                      <button onClick={() => toggleFeatured(post)} className="p-1.5 sm:p-2 text-zinc-500 hover:text-yellow-400 transition-colors rounded-lg hover:bg-zinc-800">
                        {post.featured ? <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" /> : <StarOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                      </button>
                      <button onClick={() => togglePublish(post)} className="p-1.5 sm:p-2 text-zinc-500 hover:text-emerald-400 transition-colors rounded-lg hover:bg-zinc-800">
                        {post.published ? <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /> : <EyeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                      </button>
                      <button onClick={() => onPost(post.slug)} className="p-1.5 sm:p-2 text-zinc-500 hover:text-blue-400 transition-colors rounded-lg hover:bg-zinc-800">
                        <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button onClick={() => openEdit(post.slug)} className="p-1.5 sm:p-2 text-zinc-500 hover:text-white transition-colors rounded-lg hover:bg-zinc-800">
                        <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirm(post.slug)} className="p-1.5 sm:p-2 text-zinc-500 hover:text-red-400 transition-colors rounded-lg hover:bg-zinc-800">
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* EDITOR */}
        {editing && !previewMode && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 sm:space-y-6">
            <h2 className="font-heading text-lg sm:text-xl font-bold">{editing === 'new' ? 'Nuevo post' : 'Editar post'}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="lg:col-span-2 space-y-5">
                {/* Title */}
                <div className="space-y-1.5">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Título *</label>
                  <input value={form.title} onChange={e => handleTitleChange(e.target.value)}
                    placeholder="Cómo detectar imágenes generadas por Midjourney"
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-blue-500 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 font-heading text-lg font-bold outline-none transition-colors" />
                </div>
                {/* Slug */}
                <div className="space-y-1.5">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Slug (URL) *</label>
                  <div className="flex items-center bg-zinc-900 border border-zinc-700 focus-within:border-blue-500 rounded-lg overflow-hidden transition-colors">
                    <span className="px-3 text-zinc-600 font-mono text-sm border-r border-zinc-700 py-2.5 bg-zinc-800">/blog/</span>
                    <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) }))}
                      placeholder="como-detectar-midjourney"
                      className="flex-1 bg-transparent px-3 py-2.5 text-zinc-300 font-mono text-sm outline-none" />
                  </div>
                </div>
                {/* Excerpt */}
                <div className="space-y-1.5">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Extracto / SEO *</label>
                  <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                    rows={3} placeholder="Breve descripción para SEO y tarjetas (máx. 160 caracteres)..."
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-blue-500 rounded-lg px-4 py-3 text-zinc-300 placeholder-zinc-600 font-mono text-sm outline-none transition-colors resize-none" />
                  <p className={`text-xs font-mono ${form.excerpt.length > 160 ? 'text-red-400' : 'text-zinc-600'}`}>{form.excerpt.length} / 160</p>
                </div>

                {/* Content + toolbar */}
                <div className="space-y-0">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider block mb-1.5">Contenido (Markdown) *</label>

                  {/* Toolbar */}
                  <div className="bg-zinc-800 border border-zinc-700 rounded-t-lg px-2 py-1.5 flex flex-wrap gap-1 items-center">
                    {TOOLBAR_BUTTONS.map(({ label, insert }) => (
                      <button key={label} type="button"
                        onMouseDown={e => {
                          // Prevent textarea from losing focus
                          e.preventDefault();
                          insertAtCursor(insert);
                        }}
                        className="text-zinc-500 hover:text-zinc-200 font-mono text-xs transition-colors px-1.5 py-1 rounded hover:bg-zinc-700">
                        {label}
                      </button>
                    ))}
                    <div className="w-px h-4 bg-zinc-700 mx-1" />
                    <ImageUploader
                      textareaRef={textareaRef}
                      setContent={(newContent) => setForm(f => ({ ...f, content: newContent }))}
                    />
                    <YouTubeInserter
                      textareaRef={textareaRef}
                      setContent={(newContent) => setForm(f => ({ ...f, content: newContent }))}
                    />
                  </div>

                  <textarea
                    ref={textareaRef}
                    value={form.content}
                    onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                    rows={26}
                    className="w-full bg-zinc-900 border border-zinc-700 border-t-0 focus:border-blue-500 rounded-b-lg px-4 py-3 text-zinc-300 placeholder-zinc-700 font-mono text-sm outline-none transition-colors resize-y leading-relaxed"
                    placeholder="Escribe el contenido en markdown..."
                  />
                  <p className="text-zinc-600 text-xs font-mono mt-1">
                    {form.content.split(/\s+/).filter(Boolean).length} palabras · ~{Math.max(1, Math.ceil(form.content.split(/\s+/).length / 200))} min lectura
                  </p>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Mobile: save + toggle */}
                <div className="flex gap-2 lg:hidden">
                  <button type="button" onClick={handleSave} disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white font-mono text-sm uppercase tracking-wider rounded-lg transition-all">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Guardando...' : 'Guardar post'}
                  </button>
                  <button type="button" onClick={() => setSidebarOpen(v => !v)}
                    className="flex items-center gap-1.5 px-4 py-3 border border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs rounded-lg transition-all whitespace-nowrap">
                    {sidebarOpen ? 'Ocultar ↑' : 'Opciones ↓'}
                  </button>
                </div>

                {/* Mobile estado rápido */}
                <div className="flex gap-2 lg:hidden">
                  <button type="button" onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-all font-mono text-xs ${
                      form.published ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400'
                    }`}>
                    {form.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {form.published ? 'Publicado' : 'Borrador'}
                  </button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, featured: !f.featured }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border transition-all font-mono text-xs ${
                      form.featured ? 'bg-yellow-500/10 border-yellow-500/40 text-yellow-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400'
                    }`}>
                    {form.featured ? <Star className="w-3.5 h-3.5 fill-yellow-400" /> : <StarOff className="w-3.5 h-3.5" />}
                    {form.featured ? 'Destacado' : 'Normal'}
                  </button>
                </div>

                {/* Collapsible on mobile, always visible on desktop */}
                <div className={`space-y-4 sm:space-y-5 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
                <div className="space-y-2">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Emoji de portada</label>
                  <div className="grid grid-cols-8 gap-1">
                    {EMOJIS.map(e => (
                      <button key={e} type="button" onClick={() => setForm(f => ({ ...f, cover_emoji: e }))}
                        className={`text-xl p-1.5 rounded-lg transition-all ${form.cover_emoji === e ? 'bg-blue-600' : 'hover:bg-zinc-800'}`}>{e}</button>
                    ))}
                  </div>
                  <input value={form.cover_emoji} onChange={e => setForm(f => ({ ...f, cover_emoji: e.target.value }))} maxLength={4}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-center text-2xl outline-none focus:border-blue-500 transition-colors" />
                </div>
                {/* Cover image */}
                <div className="space-y-2">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Imagen de portada</label>
                  <CoverImageUploader
                    value={form.cover_image}
                    onChange={(url) => setForm(f => ({ ...f, cover_image: url }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Categoría</label>
                  <div className="space-y-1">
                    {CATEGORIES.map(cat => (
                      <button key={cat.value} type="button" onClick={() => setForm(f => ({ ...f, category: cat.value }))}
                        className={`w-full text-left px-3 py-2 rounded-lg font-mono text-sm transition-all ${form.category === cat.value ? 'bg-blue-600 text-white' : 'bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white'}`}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Tags (separadas por comas)</label>
                  <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="ia, deepfake, tutorial"
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-blue-500 rounded-lg px-3 py-2.5 text-zinc-300 placeholder-zinc-600 font-mono text-sm outline-none transition-colors" />
                </div>
                <div className="space-y-2">
                  <label className="font-mono text-xs text-zinc-400 uppercase tracking-wider">Estado</label>
                  <button type="button" onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-all ${form.published ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400'}`}>
                    {form.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <span className="font-mono text-sm">{form.published ? 'Publicado' : 'Borrador'}</span>
                  </button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, featured: !f.featured }))}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-all ${form.featured ? 'bg-yellow-500/10 border-yellow-500/40 text-yellow-400' : 'bg-zinc-900 border-zinc-700 text-zinc-400'}`}>
                    {form.featured ? <Star className="w-4 h-4 fill-yellow-400" /> : <StarOff className="w-4 h-4" />}
                    <span className="font-mono text-sm">{form.featured ? 'Destacado' : 'No destacado'}</span>
                  </button>
                </div>
                <button type="button" onClick={handleSave} disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white font-mono text-sm uppercase tracking-wider rounded-lg transition-all">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Guardando...' : 'Guardar post'}
                </button>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Insertar imagen</p>
                  <p className="text-zinc-500 text-xs leading-relaxed">
                    1. Haz clic donde quieras insertar en el texto<br/>
                    2. Pulsa el botón <span className="text-blue-400 font-mono">Imagen</span> de la barra<br/>
                    3. Sube o pega la URL<br/>
                    4. Pulsa "Insertar en el texto"
                  </p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Sintaxis rápida</p>
                  {[['# Título','H1'],['## Sección','H2'],['**texto**','Negrita'],['*texto*','Cursiva'],['`código`','Inline'],['![alt](url)','Imagen'],['> texto','Cita'],['- item','Lista'],['---','Separador']].map(([s,d]) => (
                    <div key={s} className="flex items-center justify-between">
                      <code className="text-blue-400 font-mono text-xs">{s}</code>
                      <span className="text-zinc-600 text-xs font-mono">{d}</span>
                    </div>
                  ))}
                </div>
                </div>{/* end collapsible */}
              </div>
            </div>
          </motion.div>
        )}

        {/* PREVIEW */}
        {editing && previewMode && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto">
            <div className="mb-6 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 font-mono text-xs text-center">
              Vista previa — así se verá el post publicado
            </div>
            {form.cover_image ? (
              <div className="mb-6 rounded-2xl overflow-hidden border border-zinc-800">
                <img src={form.cover_image} alt={form.title} className="w-full max-h-64 object-cover" />
              </div>
            ) : (
              <div className="text-8xl text-center mb-6">{form.cover_emoji}</div>
            )}
            <h1 className="font-heading text-4xl font-black text-white mb-4">{form.title || 'Sin título'}</h1>
            <p className="text-zinc-400 text-xl mb-8 border-b border-zinc-800 pb-8">{form.excerpt || 'Sin extracto'}</p>
            <PreviewContent content={form.content} />
          </motion.div>
        )}
      </main>

      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-40" onClick={() => setDeleteConfirm(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-red-500/30 rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-4">
              <div className="text-center">
                <p className="text-4xl mb-3">🗑️</p>
                <h3 className="font-heading text-xl font-bold text-white">¿Eliminar post?</h3>
                <p className="text-zinc-400 text-sm mt-2">Esta acción no se puede deshacer.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white font-mono text-sm rounded-lg transition-all">Cancelar</button>
                <button onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 text-white font-mono text-sm rounded-lg transition-all">Eliminar</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
